import type { AuthSession } from '../ports';

const EXPIRY_SKEW_SECONDS = 30;

export type AuthSessionTokens = {
  accessToken: string | null;
  expiresInSeconds: number | null;
  hasRefreshToken: boolean;
};

export type AuthSessionAdapter = {
  readTokens(): Promise<AuthSessionTokens>;
  refresh(): Promise<string | null>;
  isRefreshTokenDefinitivelyRejected(error: unknown): boolean;
  clearTokens(): Promise<void>;
  runExclusively?<T>(run: () => Promise<T>): Promise<T>;
  onExternalSessionEnd?(notify: () => void): void;
};

function isAccessTokenExpiring(tokens: AuthSessionTokens): boolean {
  return tokens.expiresInSeconds != null && tokens.expiresInSeconds <= EXPIRY_SKEW_SECONDS;
}

export function createAuthSession(adapter: AuthSessionAdapter): AuthSession {
  const runExclusively = adapter.runExclusively?.bind(adapter) ?? ((run) => run());

  const sessionExpiredListeners = new Set<() => void>();
  const notifySessionExpired = (): void => {
    for (const listener of sessionExpiredListeners) {
      listener();
    }
  };

  adapter.onExternalSessionEnd?.(notifySessionExpired);

  let refreshing: Promise<string | null> | null = null;

  async function doRefresh(): Promise<string | null> {
    const tokensBeforeWaitingForExclusiveAccess = await adapter.readTokens();

    return runExclusively(async () => {
      const tokens = await adapter.readTokens();
      const anotherContextAlreadyRefreshed =
        tokens.accessToken != null &&
        tokens.accessToken !== tokensBeforeWaitingForExclusiveAccess.accessToken;
      if (anotherContextAlreadyRefreshed) {
        return tokens.accessToken;
      }

      if (!tokens.hasRefreshToken) {
        notifySessionExpired();
        return null;
      }

      try {
        return await adapter.refresh();
      } catch (error) {
        if (!adapter.isRefreshTokenDefinitivelyRejected(error)) {
          return null;
        }
        await adapter.clearTokens();
        notifySessionExpired();
        return null;
      }
    });
  }

  function refreshOrNotifySessionExpired(): Promise<string | null> {
    refreshing ??= doRefresh().finally(() => {
      refreshing = null;
    });
    return refreshing;
  }

  return {
    async getAccessToken() {
      const tokens = await adapter.readTokens();
      if (tokens.accessToken != null && !isAccessTokenExpiring(tokens)) {
        return tokens.accessToken;
      }
      if (!tokens.hasRefreshToken) {
        if (tokens.accessToken != null) {
          notifySessionExpired();
        }
        return tokens.accessToken;
      }
      return (await refreshOrNotifySessionExpired()) ?? tokens.accessToken;
    },
    refreshOrNotifySessionExpired,
    onSessionExpired(listener) {
      sessionExpiredListeners.add(listener);
      return () => {
        sessionExpiredListeners.delete(listener);
      };
    },
    async signOut() {
      await adapter.clearTokens();
    },
  };
}
